/**
 * Doc mentions in a brief's lines (slice 6d spec §5.5): `[Build plan](crew://docs/<id>)`
 * in the file. A line shows each as a chip; while it's edited it reads
 * `@Build plan`, the editor holding which doc each `@Title` names, and the
 * save writes the links back. Pure, so it's tested on its own.
 */

// A doc's id is a UUID: anything else is an ordinary link, left as written.
const MENTION =
  /\[([^\]\n]*)\]\(crew:\/\/docs\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\)/g;

export type Mention = { id: string; title: string };

/** A line as text and mentions, in order. */
export type Piece = { text: string } | { mention: Mention };

export function pieces(line: string): Piece[] {
  const out: Piece[] = [];
  let at = 0;
  for (const m of line.matchAll(MENTION)) {
    if (m.index > at) out.push({ text: line.slice(at, m.index) });
    out.push({ mention: { id: m[2].toLowerCase(), title: m[1] } });
    at = m.index + m[0].length;
  }
  if (at < line.length) out.push({ text: line.slice(at) });
  return out;
}

/** The link the file keeps for a mention. */
export const link = (m: Mention) => `[${m.title.replace(/[[\]]/g, "")}](crew://docs/${m.id})`;

/** A line as it's edited: each mention as `@Title`, and the mentions it had. */
export function toDraft(line: string): { draft: string; mentions: Mention[] } {
  const mentions: Mention[] = [];
  const draft = line.replace(MENTION, (_whole, title: string, id: string) => {
    mentions.push({ id: id.toLowerCase(), title });
    return `@${title}`;
  });
  return { draft, mentions };
}

/** Whether `draft` has `@<title>` at `at`, as a whole: no letter or digit right after it. */
function tokenAt(draft: string, at: number, title: string): boolean {
  const end = at + title.length + 1;
  return draft.startsWith(`@${title}`, at) && !/[\p{L}\p{N}]/u.test(draft[end] ?? "");
}

/**
 * The edited line as the file keeps it: each `@Title` the editor knows, its
 * link again. "@Planning" isn't "@Plan" with "ning" after it.
 */
export function fromDraft(draft: string, mentions: readonly Mention[]): string {
  // Longest first, so "@Build plan v2" isn't taken as "@Build plan".
  const known = [...mentions].sort((a, b) => b.title.length - a.title.length);
  let out = "";
  let at = 0;
  while (at < draft.length) {
    const m = draft[at] === "@" ? known.find((k) => tokenAt(draft, at, k.title)) : null;
    if (m) {
      out += link(m);
      at += m.title.length + 1;
    } else {
      out += draft[at];
      at += 1;
    }
  }
  return out;
}

/**
 * A picked doc, its `@Title` made unlike the line's other mentions': a second
 * "Plan" is "Plan (2)", so each `@Title` names one doc.
 */
export function uniqueMention(m: Mention, mentions: readonly Mention[]): Mention {
  const taken = (title: string) => mentions.some((x) => x.title === title && x.id !== m.id);
  let title = m.title;
  for (let n = 2; taken(title); n++) title = `${m.title} (${n})`;
  return { ...m, title };
}

/**
 * The `@…` being typed just before the cursor, or null: an `@` at the start or
 * after a space, then up to 40 characters with no other `@`.
 */
export function activeQuery(
  draft: string,
  cursor: number,
): { start: number; query: string } | null {
  const before = draft.slice(0, cursor);
  const m = /(?:^|\s)@([^@\n]{0,40})$/.exec(before);
  if (!m) return null;
  return { start: cursor - m[1].length - 1, query: m[1] };
}

/** The draft with the `@query` at `start..cursor` made `@Title `, and where the cursor goes. */
export function insertMention(
  draft: string,
  start: number,
  cursor: number,
  title: string,
): { draft: string; cursor: number } {
  const token = `@${title} `;
  const rest = draft.slice(cursor).replace(/^ /, "");
  return { draft: `${draft.slice(0, start)}${token}${rest}`, cursor: start + token.length };
}

type PickerDoc = {
  id: string;
  title: string;
  updated_at: string;
  area?: { id: string; label: string } | null;
};

/**
 * The picker's rows (spec §5.5): this Area's docs first, then the others with
 * their Area named, each group as the search ranked it, at most 6 in all.
 * `docs` may hold one twice (the Area's own search, then everyone's).
 */
export function pickerRows<D extends PickerDoc>(docs: readonly D[], areaId: string) {
  const once = docs.filter((d, i) => docs.findIndex((x) => x.id === d.id) === i);
  const here = once.filter((d) => d.area?.id === areaId);
  const other = once.filter((d) => d.area?.id !== areaId);
  const keep = here.slice(0, 6);
  const rest = other.slice(0, 6 - keep.length);
  return { here: keep, other: rest, top: keep[0] ?? rest[0] };
}

/** What a key does in a line's box: with the picker open, Enter picks and Escape closes it. */
export function boxKey(key: string, picking: boolean): "pick" | "save" | "close" | "cancel" | null {
  if (key === "Enter") return picking ? "pick" : "save";
  if (key === "Escape") return picking ? "close" : "cancel";
  return null;
}

/** An edit as it's saved: its `@Title`s written back as the docs' links. */
export function withLinks<E extends { draft: string }>(
  edit: E | null,
  mentions: readonly Mention[],
) {
  return edit && { ...edit, draft: fromDraft(edit.draft, mentions) };
}

/** The picker as the cursor leaves it: the `@…` being typed, unless Escape closed it there. */
export function pickerState(draft: string, cursor: number, closed: number | null) {
  const at = activeQuery(draft, cursor);
  const open = at !== null && closed !== at.start;
  return { start: at?.start ?? cursor, query: open ? at.query : null };
}

/** A line as plain words, each mention its title: for a row that's itself a link. */
export const plainMentions = (line: string) =>
  pieces(line)
    .map((p) => ("text" in p ? p.text : p.mention.title))
    .join("");
